import React, { useState, useRef, useEffect } from "react"
import PropTypes from "prop-types"
import compose from "ramda/src/compose"
import { connect } from "react-redux"
import classnames from "classnames"
import _map from "lodash/map"
import _filter from "lodash/filter"
import _get from "lodash/get"
import { Collapse } from "react-collapse"

import ThemeConsumer from "shared/components/ThemeConsumer"
import Typograph from "components/Typograph"
import TypographLink from "shared/components/TypographLink"

const SubItem = ({
  id,
  iconComponent: Icon = () => null,
  name,
  active,
  theme,
  arrowIcon: ArrowIcon = () => null,
  subItems = [],
  iconClasses,
  currentPath,
  nameBadge,
  products,
  isMonitoring,
  hasNewMenu,
  newMenuLabel,
  beegolAccess,
  customer,
  saveOperation,
  operation
}) => {
  const refItem = useRef(null)
  const refButton = useRef(null)

  const [isOpenItems, setOpenItems] = useState(false)

  const subItemsMenu = _filter(subItems, (item) => {
    const visible = item.visible
    const monitoring = item.monitoring

    const hasVisible = typeof visible !== "undefined"
    const hasMonitoring = typeof monitoring !== "undefined"

    if (!hasVisible && !hasMonitoring) {
      return true
    }

    if (hasVisible && !hasMonitoring) {
      return visible({ products, beegolAccess, customer })
    }

    if (hasMonitoring && !hasVisible) {
      return monitoring(isMonitoring)
    }

    return false
  })

  useEffect(() => {
    const listener = (e) => {
      if (e.key === "Enter") {
        refButton.current.click()
      }
    }

    refItem.current.addEventListener("keydown", listener)

    return () => refItem.current.removeEventListener("keydown", listener)
  }, [])

  const linkClasses = classnames(
    {
      [theme.containerItem]: !isOpenItems || !active,
      [theme.containerItemActive]: isOpenItems || active
    },
    theme.p3,
    theme.mb3
  )

  const buttonClasses = classnames(
    theme.w100,
    theme.containerButton,
    "u-flex u-flexAlignItemsCenter",
    {
      [theme.containerButtonOpen]: isOpenItems
    }
  )

  const iconArrowClasses = classnames(theme.mr3, theme.iconArrowColor, {
    [theme.iconArrowColor]: !active,
    [theme.iconArrowColorActive]: active || isOpenItems,
    [theme.iconToRight]: !active,
    [theme.iconArrowColorRotate]: isOpenItems
  })

  const handleClick = () => {
    const shouldTrack = _get(operation, "shouldTrack", false)

    if (shouldTrack && customer && saveOperation && operation) {
      saveOperation(
        customer,
        _get(operation, "numTelefone", ""),
        _get(operation, "type", ""),
        _get(operation, "action", "CLICK"),
        _get(operation, "integrity", "PENDING"),
        _get(operation, "protocol", ""),
        _get(operation, "obs", "")
      )
    }

    setOpenItems(!isOpenItems)
  }

  const showBadge = nameBadge && !isOpenItems

  return (
    <li tabIndex="0" ref={refItem} className={linkClasses}>
      <button
        tabIndex="-1"
        ref={refButton}
        className={buttonClasses}
        onClick={handleClick}
        id={id}
      >
        <Icon className={iconClasses} />
        <Typograph preset="item" size="small" isActive={active}>
          {name}
          {hasNewMenu && !isOpenItems && (
            <span className={classnames(theme.newItem, theme.hasNewMenu)}>
              {newMenuLabel || "Novidades"}
            </span>
          )}
        </Typograph>
        {showBadge && <span className={theme.badge}>{nameBadge}</span>}
        <ArrowIcon className={iconArrowClasses} />
      </button>
      <Collapse isOpened={isOpenItems}>
        <ul className={theme.pl9}>
          {_map(subItemsMenu, (item, index) => {
            const { name, nameBadge, route, id, isNew, operation, newLabel } =
              item
            const activeItem = currentPath === route.href

            const shouldTrack = _get(operation, "shouldTrack", false)

            const handleMenuClick = async () => {
              if (shouldTrack && customer && saveOperation && operation) {
                await saveOperation(
                  customer,
                  _get(operation, "numTelefone", ""),
                  _get(operation, "type", ""),
                  _get(operation, "action", "CLICK"),
                  _get(operation, "integrity", "PENDING"),
                  _get(operation, "protocol", ""),
                  _get(operation, "obs", "")
                )
              }
            }

            return (
              <li
                key={`${index}-${name}`}
                className={classnames(theme.containerSubItem, theme.mt3)}
              >
                <TypographLink
                  isActive={activeItem}
                  to={route.href}
                  preset="subitem"
                  size="small"
                  isExternal={route.isExternal}
                  target="_self"
                  id={id}
                  onClick={shouldTrack ? handleMenuClick : undefined}
                >
                  {isNew && isOpenItems && (
                    <span
                      className={classnames(theme.newItem, theme.positionItem)}
                    >
                      {newLabel || "Novo"}
                    </span>
                  )}
                  {name}
                </TypographLink>
                {nameBadge && <span className={theme.badge}>{nameBadge}</span>}
              </li>
            )
          })}
        </ul>
      </Collapse>
    </li>
  )
}

SubItem.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  nameBadge: PropTypes.string,
  active: PropTypes.bool,
  theme: PropTypes.shape({
    mt3: PropTypes.string,
    mb3: PropTypes.string,
    pl9: PropTypes.string,
    mr3: PropTypes.string,
    w100: PropTypes.string,
    p3: PropTypes.string,
    iconArrowColor: PropTypes.string,
    iconArrowColorActive: PropTypes.string,
    containerItem: PropTypes.string,
    containerItemActive: PropTypes.string,
    containerButton: PropTypes.string,
    iconArrowColorRotate: PropTypes.string,
    iconToRight: PropTypes.string,
    badge: PropTypes.string,
    containerSubItem: PropTypes.string,
    containerButtonOpen: PropTypes.string,
    newItem: PropTypes.string,
    positionItem: PropTypes.string,
    hasNewMenu: PropTypes.string
  }),
  iconComponent: PropTypes.func,
  subItems: PropTypes.array,
  arrowIcon: PropTypes.func,
  linkClasses: PropTypes.string,
  iconClasses: PropTypes.string,
  currentPath: PropTypes.string,
  products: PropTypes.object,
  isMonitoring: PropTypes.bool,
  hasNewMenu: PropTypes.bool,
  newMenuLabel: PropTypes.string,
  beegolAccess: PropTypes.bool,
  customer: PropTypes.object,
  saveOperation: PropTypes.func,
  operation: PropTypes.object
}

const mapStateToProps = ({ products }) => ({
  products
})

const enhance = compose(connect(mapStateToProps))

export default enhance(ThemeConsumer("UIMenu")(SubItem))



// WEBPACK FOOTER //
// ./src/components/ModernLayout/Menu/SubItem.js